const ADCASH_STORAGE_KEY = 'LENTHO_ADCASH_CLICK_ID';
const ADCASH_EXPIRY_DAYS = 30;

export type AdcashEventType = 'button_click' | 'registration' | 'sale';

export interface AdcashClickData {
  click_id: string;
  expires_at: number;
  captured_at: number;
  tracked_events: AdcashEventType[];
}

/**
 * Parse Adcash click ID from URL search params
 */
export function parseAdcashClickId(
  searchParams: URLSearchParams
): AdcashClickData | null {
  const click_id = searchParams.get('cid');

  if (!click_id) return null;

  return {
    click_id,
    expires_at: Date.now() + ADCASH_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    captured_at: Date.now(),
    tracked_events: [],
  };
}

/**
 * Save Adcash click ID to localStorage with expiration
 */
export function saveAdcashClickId(clickData: AdcashClickData): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(ADCASH_STORAGE_KEY, JSON.stringify(clickData));
    console.log('[Adcash] Click ID saved:', clickData.click_id);
  } catch (error) {
    console.error('[Adcash] Error saving click ID:', error);
  }
}

/**
 * Get Adcash click data from localStorage if not expired
 */
function getAdcashClickData(): AdcashClickData | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem(ADCASH_STORAGE_KEY);
    if (!stored) return null;

    const data: AdcashClickData = JSON.parse(stored);

    // Backward compatibility: add tracked_events if missing
    if (!data.tracked_events) {
      data.tracked_events = [];
    }

    // Check expiration
    if (Date.now() > data.expires_at) {
      clearAdcashClickId();
      console.log('[Adcash] Click ID expired');
      return null;
    }

    return data;
  } catch (error) {
    console.error('[Adcash] Error getting click data:', error);
    return null;
  }
}

/**
 * Get Adcash click ID from localStorage if not expired
 */
export function getAdcashClickId(): string | null {
  const data = getAdcashClickData();
  return data?.click_id || null;
}

/**
 * Clear Adcash click ID from localStorage
 */
export function clearAdcashClickId(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(ADCASH_STORAGE_KEY);
    console.log('[Adcash] Click ID cleared');
  } catch (error) {
    console.error('[Adcash] Error clearing click ID:', error);
  }
}

/**
 * Check if an event type has already been tracked for the current click ID
 */
export function isEventAlreadyTracked(eventType: AdcashEventType): boolean {
  const data = getAdcashClickData();
  if (!data) return false;

  return data.tracked_events.includes(eventType);
}

/**
 * Mark an event type as tracked for the current click ID
 */
export function markEventAsTracked(eventType: AdcashEventType): void {
  if (typeof window === 'undefined') return;

  try {
    const data = getAdcashClickData();
    if (!data) return;

    // Add event type if not already tracked
    if (!data.tracked_events.includes(eventType)) {
      data.tracked_events.push(eventType);
      localStorage.setItem(ADCASH_STORAGE_KEY, JSON.stringify(data));
      console.log('[Adcash] Event marked as tracked:', eventType);
    }
  } catch (error) {
    console.error('[Adcash] Error marking event as tracked:', error);
  }
}

/**
 * Get click ID and check if event can be tracked
 * Returns click ID only if event hasn't been tracked yet
 */
export function getClickIdForEvent(eventType: AdcashEventType): string | null {
  if (isEventAlreadyTracked(eventType)) {
    console.log(`[Adcash] Event type "${eventType}" already tracked, skipping`);
    return null;
  }

  return getAdcashClickId();
}
